import React, { useState, useEffect, useCallback } from 'react';
import { socialPublishAPI } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const PLATFORMS = [
  { id: 'twitter', name: 'Twitter / X', icon: '𝕏', color: '#1da1f2' },
  { id: 'facebook', name: 'Facebook', icon: 'f', color: '#1877f2' },
  { id: 'linkedin', name: 'LinkedIn', icon: 'in', color: '#0a66c2' },
];

export default function AdminSocialPublishing() {
  const [providers, setProviders] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('accounts');
  const { toast } = useToast();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [provRes, histRes] = await Promise.all([
        socialPublishAPI.listProviders(),
        socialPublishAPI.history({ limit: 30 }),
      ]);
      setProviders(provRes.data?.providers || provRes.data || []);
      setHistory(histRes.data?.history || histRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleConnect = (provider) => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL || ''}/api/social/connect/${provider}`;
  };

  const handleDisconnect = async (provider) => {
    if (!window.confirm(`Disconnect ${provider}?`)) return;
    try {
      await socialPublishAPI.disconnect(provider);
      toast({ title: `${provider} disconnected` });
      loadData();
    } catch (e) { toast({ title: 'Error', variant: 'destructive' }); }
  };

  if (loading) return <div style={{ padding: 32, color: '#999' }}>Loading…</div>;

  const connectedIds = providers.map(p => p.provider || p.id);

  return (
    <div style={{ padding: '24px 32px' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: '#e8e8e8', margin: '0 0 4px' }}>Social Publishing</h1>
      <p style={{ color: '#888', fontSize: 14, marginBottom: 24 }}>Connect and publish directly from the CMS</p>

      <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid #2a2b2e' }}>
        {['accounts', 'history'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding: '8px 20px', background: 'none', border: 'none', color: tab === t ? '#b89b5e' : '#888',
              borderBottom: tab === t ? '2px solid #b89b5e' : '2px solid transparent', cursor: 'pointer', fontWeight: 600, textTransform: 'capitalize' }}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'accounts' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 16 }}>
          {PLATFORMS.map(p => {
            const connected = connectedIds.includes(p.id);
            return (
              <div key={p.id} style={{ background: '#1a1b1e', borderRadius: 12, padding: 24, border: `1px solid ${connected ? p.color + '40' : '#2a2b2e'}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: p.color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', color: p.color, fontWeight: 800, fontSize: 18 }}>{p.icon}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#e8e8e8', fontWeight: 600 }}>{p.name}</div>
                    <span style={{ fontSize: 11, color: connected ? '#1f7a5a' : '#666' }}>{connected ? '● Connected' : '○ Not connected'}</span>
                  </div>
                </div>
                <button onClick={() => connected ? handleDisconnect(p.id) : handleConnect(p.id)}
                  style={{ width: '100%', background: connected ? '#b3261e22' : p.color, color: connected ? '#e74c3c' : '#fff', border: connected ? '1px solid #b3261e44' : 'none', borderRadius: 8, padding: '8px 0', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                  {connected ? 'Disconnect' : `Connect ${p.name}`}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'history' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {history.map((h, i) => (
            <div key={h.id || i} style={{ background: '#1a1b1e', borderRadius: 8, padding: '12px 20px', border: '1px solid #2a2b2e', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: '#e8e8e8', fontWeight: 500 }}>{h.article_title || 'Article'}</div>
                <div style={{ color: '#888', fontSize: 12, marginTop: 2 }}>{h.provider} · {h.published_at ? new Date(h.published_at).toLocaleString() : ''}</div>
              </div>
              <span style={{ fontSize: 11, fontWeight: 600, color: h.status === 'success' ? '#1f7a5a' : '#b3261e', padding: '4px 10px', borderRadius: 12, background: h.status === 'success' ? 'rgba(31,122,90,0.15)' : 'rgba(179,38,30,0.15)' }}>
                {h.status || 'sent'}
              </span>
            </div>
          ))}
          {history.length === 0 && <div style={{ color: '#666', textAlign: 'center', padding: 40 }}>No publishing history yet.</div>}
        </div>
      )}
    </div>
  );
}
